import { useId } from 'react';
import {
  countActiveVideoFilters,
  hasActiveVideoFilters,
  type VideoFilterOptions,
  type VideoFilters,
} from '../types/prospect-media';
import { tourAnchor, type TourAnchorId } from '../features/tours/tourAnchors';
import './VideoFilterBar.css';

interface VideoFilterBarProps {
  filters: VideoFilters;
  onChange: (next: VideoFilters) => void;
  onClear: () => void;
  options: VideoFilterOptions;
  optionsLoading: boolean;
  /**
   * Total matches across the library, not the page. Reporting the rendered length instead
   * caps the number at the page size, so a search matching 300 confidently reports 100.
   */
  resultCount: number;
  /**
   * How many are actually rendered. When it is short of `resultCount` the bar says so —
   * a count the user cannot scroll to reads as a bug.
   */
  shownCount?: number;
  /** Hides the team and uploader pickers when the list is already one prospect's gallery. */
  compact?: boolean;
  /**
   * Tags the controls for the Video page tour. Only the org-wide /video page passes it: the same
   * bar sits in a player's gallery, where a video tour step has nothing to explain.
   */
  withTourAnchors?: boolean;
}

/**
 * The filter surface above a video list: free-text search, tag pills, and — on the
 * org-wide list — team, uploader, and a capture-date range.
 *
 * Every control writes the whole filter object back to the caller, which owns the state
 * and the fetch. Nothing here filters in place: the API does the narrowing, so a match
 * on page 3 isn't invisible just because the client only holds page 1.
 */
export const VideoFilterBar = ({
  filters,
  onChange,
  onClear,
  options,
  optionsLoading,
  resultCount,
  shownCount,
  compact = false,
  withTourAnchors = false,
}: VideoFilterBarProps) => {
  const id = useId();
  const anchor = (anchorId: TourAnchorId) => (withTourAnchors ? tourAnchor(anchorId) : {});
  const activeCount = countActiveVideoFilters(filters);
  const isActive = hasActiveVideoFilters(filters);

  const toggleTag = (tagId: string) => {
    onChange({
      ...filters,
      tagIds: filters.tagIds.includes(tagId)
        ? filters.tagIds.filter((existing) => existing !== tagId)
        : [...filters.tagIds, tagId],
    });
  };

  // A cleared <select> or date input reports "", which must become null rather than an
  // empty string — the query builder omits null and would otherwise send `teamId=`.
  const blankToNull = (value: string) => (value === '' ? null : value);

  return (
    <div className="video-filter-bar">
      <div className="video-filter-bar__row">
        <div className="video-filter-bar__search">
          <label className="video-filter-bar__sr-only" htmlFor={`${id}-search`}>
            Search videos
          </label>
          <input
            id={`${id}-search`}
            className="video-filter-bar__search-input"
            type="search"
            value={filters.searchTerm}
            placeholder={
              compact
                ? 'Search by title or tag'
                : 'Search by player, title, tag, or who filmed it'
            }
            onChange={(e) => onChange({ ...filters, searchTerm: e.target.value })}
            {...anchor('video.search')}
          />
        </div>

        {!compact && (
          <>
            <div className="video-filter-bar__field" {...anchor('video.filters')}>
              <label className="video-filter-bar__label" htmlFor={`${id}-team`}>
                Team
              </label>
              <select
                id={`${id}-team`}
                className="video-filter-bar__select"
                value={filters.teamId ?? ''}
                disabled={optionsLoading}
                onChange={(e) => onChange({ ...filters, teamId: blankToNull(e.target.value) })}
              >
                <option value="">All teams</option>
                {options.teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="video-filter-bar__field">
              <label className="video-filter-bar__label" htmlFor={`${id}-uploader`}>
                Filmed by
              </label>
              <select
                id={`${id}-uploader`}
                className="video-filter-bar__select"
                value={filters.uploadedBy ?? ''}
                disabled={optionsLoading}
                onChange={(e) =>
                  onChange({ ...filters, uploadedBy: blankToNull(e.target.value) })
                }
              >
                <option value="">Anyone</option>
                {options.uploaders.map((uploader) => (
                  <option key={uploader.id} value={uploader.id}>
                    {uploader.name} ({uploader.videoCount})
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="video-filter-bar__field">
          <label className="video-filter-bar__label" htmlFor={`${id}-after`}>
            From
          </label>
          <input
            id={`${id}-after`}
            className="video-filter-bar__date"
            type="date"
            value={filters.uploadedAfter ?? ''}
            max={filters.uploadedBefore ?? undefined}
            onChange={(e) => onChange({ ...filters, uploadedAfter: blankToNull(e.target.value) })}
          />
        </div>

        <div className="video-filter-bar__field">
          <label className="video-filter-bar__label" htmlFor={`${id}-before`}>
            To
          </label>
          <input
            id={`${id}-before`}
            className="video-filter-bar__date"
            type="date"
            value={filters.uploadedBefore ?? ''}
            min={filters.uploadedAfter ?? undefined}
            onChange={(e) => onChange({ ...filters, uploadedBefore: blankToNull(e.target.value) })}
          />
        </div>

        {isActive && (
          <button type="button" className="video-filter-bar__clear" onClick={onClear}>
            Clear {activeCount === 1 ? 'filter' : `${activeCount} filters`}
          </button>
        )}
      </div>

      {options.tags.length > 0 && (
        <div
          className="video-filter-bar__tags"
          role="group"
          aria-label="Filter by tag"
          {...anchor('video.tags')}
        >
          {options.tags.map((tag) => {
            const selected = filters.tagIds.includes(tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                className={`video-filter-bar__tag ${
                  selected ? 'video-filter-bar__tag--selected' : ''
                }`}
                aria-pressed={selected}
                onClick={() => toggleTag(tag.id)}
              >
                {tag.name}
              </button>
            );
          })}
        </div>
      )}

      {isActive && (
        <p className="video-filter-bar__count" role="status">
          {resultCount === 1 ? '1 video' : `${resultCount} videos`} match
          {shownCount != null && shownCount < resultCount
            ? ` — showing the ${shownCount} most recent`
            : ''}
        </p>
      )}
    </div>
  );
};
